import { useQuery } from '@apollo/client';
import { Document, Page, usePDF } from '@react-pdf/renderer';
import React, { memo, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import AUTOCOMPLETE_PRODUCT_LABELS from 'api/queries/AUTOCOMPLETE_PRODUCT_LABELS';
import GET_PRODUCT from 'api/queries/GET_PRODUCT';
import GET_SUPPLIER from 'api/queries/GET_SUPPLIER';
import DownloadSvg from 'images/v5/icons/download-01.svg';
import PrinterSvg from 'images/v5/icons/printer.svg';
import Button from 'layout/atoms/Button/Button';
import Label from 'layout/atoms/Label';
import Loader from 'layout/atoms/Loader/Loader';
import Modal from 'layout/atoms/Modal/Modal';
import PdfPreview from 'organisms/PdfGenerator/Previews/PdfPreview';
import { parseProduct } from 'parsers/specification/parseProduct';
import { selectModalByName } from 'store/layoutSlice';
import ComponentPdfPage from 'views/SpecificationDocument/DocumentView/Common/ComponentPdfPage';
function ComponentPdfPreview() {
    var _a, _b;
    const { t, i18n } = useTranslation(['specification', 'common']);
    const modal = useSelector(selectModalByName('componentPdfPreview'));
    const componentId = modal === null || modal === void 0 ? void 0 : modal.id;
    const { data, loading, error } = useQuery(GET_PRODUCT, {
        variables: { productId: componentId },
        skip: !componentId,
    });
    const { data: productLabelsData, loading: labelsLoading } = useQuery(AUTOCOMPLETE_PRODUCT_LABELS, {
        skip: !componentId,
    });
    const supplierId = (_b = (_a = data === null || data === void 0 ? void 0 : data.node) === null || _a === void 0 ? void 0 : _a.supplier) === null || _b === void 0 ? void 0 : _b.id;
    const { data: supplierData, loading: supplierLoading } = useQuery(GET_SUPPLIER, {
        variables: { id: supplierId },
        skip: !supplierId,
    });
    const component = useMemo(() => {
        var _a;
        if (!(data === null || data === void 0 ? void 0 : data.node) || !((_a = productLabelsData === null || productLabelsData === void 0 ? void 0 : productLabelsData.autocomplete) === null || _a === void 0 ? void 0 : _a.productLabels)) {
            return null;
        }
        return parseProduct(data.node, i18n, productLabelsData.autocomplete.productLabels);
    }, [data, i18n, productLabelsData]);
    const emptyDocument = useMemo(() => (React.createElement(Document, null,
        React.createElement(Page, { size: "A4" }))), []);
    const document = useMemo(() => component ? (React.createElement(Document, { language: "Polish", title: t('common:pdf') },
        React.createElement(ComponentPdfPage, { component: component, supplierDetails: supplierData === null || supplierData === void 0 ? void 0 : supplierData.node, t: t, i18n: i18n }))) : (emptyDocument), [component, emptyDocument, i18n, supplierData === null || supplierData === void 0 ? void 0 : supplierData.node, t]);
    const [{ loading: pdfLoading, url, error: pdfError }, updatePdf] = usePDF({ document });
    useEffect(() => {
        updatePdf(document);
    }, [document, updatePdf]);
    const downloadName = (component === null || component === void 0 ? void 0 : component.name) ? `Komponent__${component.name}.pdf` : 'Komponent.pdf';
    const combinedError = error || pdfError;
    const errorMessage = typeof combinedError === 'string' ? combinedError : combinedError === null || combinedError === void 0 ? void 0 : combinedError.message;
    return (React.createElement(Modal, { name: "componentPdfPreview", size: "fullScreen", noPadding: true, header: React.createElement(Label, { weight: "strong" },
            React.createElement(PrinterSvg, null),
            " ",
            t('common:pdf'),
            (component === null || component === void 0 ? void 0 : component.name) ? ` - ${component.name}` : ''), options: url ? (React.createElement("a", { href: url, className: "download", download: downloadName },
            React.createElement(Button, { color: "primary", leftIcon: React.createElement(DownloadSvg, null) }, t('common:download')))) : null },
        (loading || labelsLoading || supplierLoading || pdfLoading || !url) && React.createElement(Loader, null),
        (error || pdfError) && errorMessage && (React.createElement("div", null, t('common:error_with_message', { errorMessage }))),
        !loading && !labelsLoading && !pdfLoading && url && !pdfError && !error && (React.createElement(PdfPreview, { file: url, name: (component === null || component === void 0 ? void 0 : component.name) || t('common:pdf'), isExtended: true }))));
}
export default memo(ComponentPdfPreview);
